import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';

export interface NodeData {
  id: string;
  label: string;
  color: string;
  description: string;
  details: string;
}

interface Node extends NodeData {
  x: number;
  y: number;
  angle: number;
  radius: number;
}

const nodeColors = [
  { id: 'revenue', color: 'rgba(255, 236, 179, 0.95)' },
  { id: 'ebitda', color: 'rgba(179, 229, 252, 0.95)' },
  { id: 'capex', color: 'rgba(200, 230, 201, 0.95)' },
  { id: 'fcf', color: 'rgba(255, 205, 210, 0.95)' },
  { id: 'discount', color: 'rgba(209, 196, 233, 0.95)' },
  { id: 'wacc', color: 'rgba(255, 224, 178, 0.95)' },
  { id: 'terminal', color: 'rgba(162, 222, 208, 0.95)' },
  { id: 'npv', color: 'rgba(255, 241, 118, 0.95)' },
];

export const useFinancialNodes = (): NodeData[] => {
  const { t } = useLanguage();
  return useMemo(() => nodeColors.map(({ id, color }) => ({
    id,
    label: t(`node.${id}.label`),
    color,
    description: t(`node.${id}.description`),
    details: t(`node.${id}.details`),
  })), [t]);
};

// Keep static export for backward compat (English fallback)
export const financialNodes: NodeData[] = nodeColors.map(({ id, color }) => ({
  id,
  label: id.charAt(0).toUpperCase() + id.slice(1),
  color,
  description: '',
  details: '',
}));

interface FinancialConstellationProps {
  onNodeClick?: (node: NodeData) => void;
}

const FinancialConstellation = ({ onNodeClick }: FinancialConstellationProps) => {
  const isMobile = useIsMobile();
  const localizedNodes = useFinancialNodes();
  const containerRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  // Update dimensions on resize
  useEffect(() => {
    let rafId: number;
    const updateDimensions = () => {
      rafId = requestAnimationFrame(() => {
        if (containerRef.current) {
          setDimensions({
            width: containerRef.current.offsetWidth,
            height: containerRef.current.offsetHeight
          });
        }
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Calculate node positions in a circle around center
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;

    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    const baseRadius = isMobile 
      ? Math.min(dimensions.width, dimensions.height) * 0.32
      : Math.min(dimensions.width, dimensions.height) * 0.38;

    const calculatedNodes = localizedNodes.map((node, index) => {
      const angle = (index / localizedNodes.length) * 2 * Math.PI - Math.PI / 2;
      const radiusVariation = 1 + (Math.sin(index * 1.5) * 0.08);
      const radius = baseRadius * radiusVariation;
      
      return {
        ...node,
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
        angle,
        radius,
      };
    });

    setNodes(calculatedNodes);
  }, [dimensions, isMobile, localizedNodes]);

  const centerX = dimensions.width / 2;
  const centerY = dimensions.height / 2;

  // Handle node click
  const handleNodeClick = useCallback((node: Node) => {
    if (onNodeClick) {
      onNodeClick(node);
    }
  }, [onNodeClick]);

  return (
    <div 
      ref={containerRef}
      className="absolute inset-0"
      style={{ zIndex: 20 }}
    >
      {/* SVG for connection lines */}
      <svg 
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 21 }}
      >
        <defs>
          {/* Gradient for connection lines */}
          {nodes.map((node) => (
            <linearGradient
              key={`gradient-${node.id}`}
              id={`line-gradient-${node.id}`}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor={node.color} stopOpacity="0.4" />
              <stop offset="50%" stopColor={node.color} stopOpacity="0.9" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.9)" stopOpacity="0.7" />
            </linearGradient>
          ))}
          
          {/* Glow filters */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          <filter id="glow-strong" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Connection lines from nodes to center */}
        {nodes.map((node) => {
          const isHovered = hoveredNode === node.id;
          
          return (
            <g key={`connection-${node.id}`}>
              {/* Base dashed line - always visible */}
              <line
                x1={node.x}
                y1={node.y}
                x2={centerX}
                y2={centerY}
                stroke={node.color}
                strokeWidth={isMobile ? 1 : 1.5}
                strokeOpacity={0.3}
                strokeDasharray="4 4"
              />
              
              {/* Highlighted solid line on hover */}
              <line
                x1={node.x}
                y1={node.y}
                x2={centerX}
                y2={centerY}
                stroke={`url(#line-gradient-${node.id})`}
                strokeWidth={isHovered ? (isMobile ? 4 : 6) : (isMobile ? 1.5 : 2)}
                strokeOpacity={isHovered ? 1 : 0.5}
                filter={isHovered ? "url(#glow-strong)" : "url(#glow)"}
                className="transition-all duration-200"
              />
              
              {/* Data flow particle - same speed for all, faster on hover */}
              <circle 
                r={isHovered ? (isMobile ? 5 : 7) : (isMobile ? 3 : 4)} 
                fill={node.color} 
                filter={isHovered ? "url(#glow-strong)" : "url(#glow)"}
                opacity={isHovered ? 1 : 0.7}
              >
                <animateMotion
                  dur="1.5s"
                  repeatCount="indefinite"
                  path={`M${node.x},${node.y} L${centerX},${centerY}`}
                />
              </circle>
            </g>
          );
        })}

        {/* Central node glow ring */}
        <circle
          cx={centerX}
          cy={centerY}
          r={hoveredNode ? (isMobile ? 28 : 42) : (isMobile ? 22 : 34)}
          fill="none"
          stroke={hoveredNode ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.35)"}
          strokeWidth={hoveredNode ? 3 : 2}
          filter={hoveredNode ? "url(#glow-strong)" : "url(#glow)"}
          className="transition-all duration-200"
        />
      </svg>

      {/* Outer nodes (financial inputs) - NO TOOLTIPS, just click to open dialog */}
      {nodes.map((node) => {
        const isHovered = hoveredNode === node.id;
        
        return (
          <button
            key={node.id}
            type="button"
            className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-lg group"
            style={{
              left: node.x,
              top: node.y,
              zIndex: isHovered ? 50 : 25,
              animation: `float-node 3s ease-in-out infinite`,
            }}
            onMouseEnter={() => setHoveredNode(node.id)}
            onMouseLeave={() => setHoveredNode(null)}
            onClick={() => handleNodeClick(node)}
            aria-label={`Learn more about ${node.label}`}
          >
            <div
              className="px-2 py-1 md:px-3 md:py-1.5 rounded-lg backdrop-blur-sm font-semibold text-center whitespace-nowrap transition-all duration-200 relative"
              style={{
                backgroundColor: isHovered 
                  ? node.color.replace('0.95', '1') 
                  : node.color.replace('0.95', '0.85'),
                color: '#1a365d',
                fontSize: isMobile ? '9px' : '12px',
                boxShadow: isHovered 
                  ? `0 0 30px ${node.color}, 0 0 50px ${node.color.replace('0.95', '0.5')}, 0 8px 25px rgba(0,0,0,0.4)` 
                  : `0 0 15px ${node.color.replace('0.95', '0.3')}, 0 4px 12px rgba(0,0,0,0.25)`,
                border: isHovered 
                  ? '2px solid rgba(255,255,255,1)' 
                  : '1px solid rgba(255,255,255,0.5)',
                transform: isHovered ? 'scale(1.15)' : 'scale(1)',
              }}
            >
              {node.label}
              {/* Small info icon indicator on hover */}
              {isHovered && (
                <span 
                  className="absolute -top-1 -right-1 w-4 h-4 bg-background rounded-full flex items-center justify-center text-primary shadow-lg"
                  style={{ fontSize: '10px', fontWeight: 'bold' }}
                >
                  ?
                </span>
              )}
            </div>
          </button>
        );
      })}

      {/* Floating animation keyframes */}
      <style>{`
        @keyframes float-node {
          0%, 100% {
            transform: translate(-50%, -50%) translateY(0px);
          }
          50% {
            transform: translate(-50%, -50%) translateY(-5px);
          }
        }
      `}</style>
    </div>
  );
};

export default FinancialConstellation;
